import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Typography theme system.
 *
 * Allows each user to customize the font family used for body text, headings/serif and code,
 * plus a global type scale (S/M/L/XL) that proportionally resizes the whole app.
 *
 * The current preferences are persisted on the `profiles` row and applied at runtime by
 * setting CSS variables (`--font-sans`, `--font-serif`, `--font-mono`, `--font-scale`)
 * on `<html>`. The Tailwind `fontFamily` utilities and the global `font-size` of `<html>`
 * pick those variables up automatically.
 */

export type FontScale = "S" | "M" | "L" | "XL";

/** Catalog of fonts available in the appearance picker. Each entry knows whether it can be loaded from Google Fonts. */
export type FontFamilyChoice = {
  /** Stable identifier stored in DB. */
  id: string;
  /** Human-friendly label shown in the UI. */
  label: string;
  /** Full CSS font-family stack used at render time. */
  stack: string;
  /** Category used to filter the picker. */
  category: "sans" | "serif" | "mono";
  /** When true, we lazy-load this family from Google Fonts. */
  google?: boolean;
};

/**
 * Curated font catalog. We intentionally limit the list to keep the picker focused
 * and avoid pulling dozens of unused fonts from Google Fonts at runtime.
 */
export const FONT_CATALOG: FontFamilyChoice[] = [
  // System / safe fonts (no network needed).
  { id: "system", label: "System UI",  stack: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", category: "sans" },
  { id: "Georgia", label: "Georgia",   stack: "Georgia, 'Times New Roman', serif", category: "serif" },

  // Google Fonts — sans
  { id: "Inter",        label: "Inter",        stack: "'Inter', ui-sans-serif, system-ui, sans-serif",        category: "sans",  google: true },
  { id: "Poppins",      label: "Poppins",      stack: "'Poppins', ui-sans-serif, system-ui, sans-serif",      category: "sans",  google: true },
  { id: "Manrope",      label: "Manrope",      stack: "'Manrope', ui-sans-serif, system-ui, sans-serif",      category: "sans",  google: true },
  { id: "DM Sans",      label: "DM Sans",      stack: "'DM Sans', ui-sans-serif, system-ui, sans-serif",      category: "sans",  google: true },
  { id: "Nunito",       label: "Nunito",       stack: "'Nunito', ui-sans-serif, system-ui, sans-serif",       category: "sans",  google: true },

  // Google Fonts — serif
  { id: "Playfair Display", label: "Playfair Display", stack: "'Playfair Display', Georgia, serif", category: "serif", google: true },
  { id: "Merriweather",     label: "Merriweather",     stack: "'Merriweather', Georgia, serif",     category: "serif", google: true },
  { id: "Lora",             label: "Lora",             stack: "'Lora', Georgia, serif",             category: "serif", google: true },

  // Google Fonts — mono
  { id: "JetBrains Mono", label: "JetBrains Mono", stack: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace", category: "mono", google: true },
  { id: "Fira Code",      label: "Fira Code",      stack: "'Fira Code', ui-monospace, SFMono-Regular, Menlo, monospace",      category: "mono", google: true },
  { id: "IBM Plex Mono",  label: "IBM Plex Mono",  stack: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",  category: "mono", google: true },
];

/** Maps the discrete scale stored in DB to an actual ratio applied on the root `<html>` font-size. */
export const FONT_SCALE_RATIOS: Record<FontScale, number> = {
  S: 0.875,  // 14px base
  M: 1,      // 16px base — default
  L: 1.125,  // 18px base
  XL: 1.25,  // 20px base
};

export type ThemePreferences = {
  fontSans: string;
  fontSerif: string;
  fontMono: string;
  fontScale: FontScale;
};

const DEFAULT_PREFS: ThemePreferences = {
  fontSans: "Inter",
  fontSerif: "Georgia",
  fontMono: "JetBrains Mono",
  fontScale: "S",
};

type ThemeContextValue = {
  /** Current preferences applied to the document (may include unsaved preview changes). */
  prefs: ThemePreferences;
  /** Last preferences persisted in DB. Used to detect unsaved changes and to revert. */
  savedPrefs: ThemePreferences;
  /** True when `prefs` differs from `savedPrefs` (unsaved live preview). */
  isDirty: boolean;
  /** Live update without persistence — used for previewing changes inside the settings panel. */
  preview: (next: Partial<ThemePreferences>) => void;
  /** Persist the current `prefs` to the `profiles` row. Returns true on success. */
  save: (next?: Partial<ThemePreferences>) => Promise<boolean>;
  /** Discard unsaved preview changes and revert to `savedPrefs`. */
  discard: () => void;
  /** Restore the default preset and persist it. */
  reset: () => Promise<boolean>;
  loading: boolean;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/** Find a font definition in the catalog, falling back to the system stack if it was removed/renamed. */
function resolveFont(id: string, fallbackCategory: FontFamilyChoice["category"]): FontFamilyChoice {
  return (
    FONT_CATALOG.find((f) => f.id === id) ??
    FONT_CATALOG.find((f) => f.category === fallbackCategory) ??
    FONT_CATALOG[0]
  );
}

/**
 * Inject a single Google Fonts <link> for the requested family if it isn't already loaded.
 * We deduplicate on the family id so re-renders don't spam the document head.
 */
const loadedGoogleFonts = new Set<string>();
function ensureGoogleFontLoaded(font: FontFamilyChoice) {
  if (!font.google) return;
  if (loadedGoogleFonts.has(font.id)) return;
  const family = font.id.replace(/ /g, "+");
  const href = `https://fonts.googleapis.com/css2?family=${family}:wght@300;400;500;600;700&display=swap`;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.dataset.themeFont = font.id;
  document.head.appendChild(link);
  loadedGoogleFonts.add(font.id);
}

/** Apply a complete preferences set to the document root. Pure DOM side-effect, no React state involved. */
function applyToDocument(prefs: ThemePreferences) {
  const sans = resolveFont(prefs.fontSans, "sans");
  const serif = resolveFont(prefs.fontSerif, "serif");
  const mono = resolveFont(prefs.fontMono, "mono");

  ensureGoogleFontLoaded(sans);
  ensureGoogleFontLoaded(serif);
  ensureGoogleFontLoaded(mono);

  const root = document.documentElement;
  root.style.setProperty("--font-sans", sans.stack);
  root.style.setProperty("--font-serif", serif.stack);
  root.style.setProperty("--font-mono", mono.stack);
  root.style.setProperty("--font-scale", String(FONT_SCALE_RATIOS[prefs.fontScale]));
  root.dataset.fontScale = prefs.fontScale;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<ThemePreferences>(DEFAULT_PREFS);
  const [savedPrefs, setSavedPrefs] = useState<ThemePreferences>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  // When previewing unsaved changes, we want subsequent server fetches to NOT clobber the live edit.
  const previewingRef = useRef(false);

  // Load preferences from the user's profile on every auth change.
  // This guarantees each coach sees their own typography settings the moment
  // they log in, even when another coach previously used the same browser tab.
  useEffect(() => {
    let cancelled = false;
    // Any unsaved preview from a previous session must NOT survive an account
    // switch — otherwise we'd refuse to apply the new coach's saved prefs.
    previewingRef.current = false;

    async function load() {
      if (!user) {
        // Logged out: revert to defaults so the auth screen has a clean baseline.
        setPrefs(DEFAULT_PREFS);
        setSavedPrefs(DEFAULT_PREFS);
        applyToDocument(DEFAULT_PREFS);
        setLoading(false);
        return;
      }
      setLoading(true);
      const { data, error } = await supabase
        .from("profiles")
        .select("theme_font_sans, theme_font_serif, theme_font_mono, theme_font_scale")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        applyToDocument(DEFAULT_PREFS);
        setPrefs(DEFAULT_PREFS);
        setSavedPrefs(DEFAULT_PREFS);
      } else {
        const next: ThemePreferences = {
          fontSans: data.theme_font_sans ?? DEFAULT_PREFS.fontSans,
          fontSerif: data.theme_font_serif ?? DEFAULT_PREFS.fontSerif,
          fontMono: data.theme_font_mono ?? DEFAULT_PREFS.fontMono,
          fontScale: ((data.theme_font_scale as FontScale) ?? DEFAULT_PREFS.fontScale),
        };
        setSavedPrefs(next);
        // Always apply on auth change — previewingRef was just reset above,
        // so we know there's no in-flight unsaved edit to preserve.
        setPrefs(next);
        applyToDocument(next);
      }
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const preview = useCallback((next: Partial<ThemePreferences>) => {
    previewingRef.current = true;
    setPrefs((prev) => {
      const merged = { ...prev, ...next };
      applyToDocument(merged);
      return merged;
    });
  }, []);

  const discard = useCallback(() => {
    previewingRef.current = false;
    setPrefs(savedPrefs);
    applyToDocument(savedPrefs);
  }, [savedPrefs]);

  const save = useCallback(
    async (next?: Partial<ThemePreferences>): Promise<boolean> => {
      if (!user) return false;
      const merged = { ...prefs, ...(next ?? {}) };
      // Optimistic UI: apply immediately, then persist.
      setPrefs(merged);
      applyToDocument(merged);
      previewingRef.current = false;
      const { error } = await supabase
        .from("profiles")
        .update({
          theme_font_sans: merged.fontSans,
          theme_font_serif: merged.fontSerif,
          theme_font_mono: merged.fontMono,
          theme_font_scale: merged.fontScale,
        })
        .eq("id", user.id);
      if (error) {
        console.error("ThemeProvider.save failed", error);
        return false;
      }
      setSavedPrefs(merged);
      return true;
    },
    [user, prefs],
  );

  const reset = useCallback(async () => save(DEFAULT_PREFS), [save]);

  const isDirty = useMemo(
    () =>
      prefs.fontSans !== savedPrefs.fontSans ||
      prefs.fontSerif !== savedPrefs.fontSerif ||
      prefs.fontMono !== savedPrefs.fontMono ||
      prefs.fontScale !== savedPrefs.fontScale,
    [prefs, savedPrefs],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ prefs, savedPrefs, isDirty, preview, discard, save, reset, loading }),
    [prefs, savedPrefs, isDirty, preview, discard, save, reset, loading],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
